import { isAxiosError } from "axios";
import { t } from "i18next";
import axios from "./axios";

export type EmploymentSpan = {
  // Daty w formacie ISO (np. "2026-07-12T00:00:00"). `to === null` => umowa bez daty końca (etat otwarty).
  from: string;
  to: string | null;
};

export type EmploymentPeriods = {
  companyId: number;
  employeeId: number;
  spans: EmploymentSpan[];
};

// Klucz indeksu przedziałów zatrudnienia: "companyId-employeeId".
export type EmploymentIndex = Map<string, EmploymentSpan[]>;

export const employmentKey = (companyId: number, employeeId: number): string =>
  `${companyId}-${employeeId}`;

// yyyy-MM-dd (lokalnie) dla Date albo dla stringa ISO/daty.
const toDayString = (date: Date | string): string => {
  if (typeof date === "string") {
    return date.slice(0, 10);
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Czy dzień mieści się w którymkolwiek przedziale zatrudnienia.
 * Gdy `spans` jest undefined (brak danych o zatrudnieniu dla pracownika) zwraca true (nie blokujemy - fail-open,
 * spójnie z backendem).
 */
export const isDayWithinEmployment = (
  spans: EmploymentSpan[] | undefined,
  date: Date | string,
): boolean => {
  if (!spans) {
    return true;
  }
  const day = toDayString(date);
  return spans.some((s) => {
    const from = s.from.slice(0, 10);
    const to = s.to ? s.to.slice(0, 10) : null;
    return from <= day && (to === null || day <= to);
  });
};

export const buildEmploymentIndex = (
  periods: EmploymentPeriods[],
): EmploymentIndex => {
  const index: EmploymentIndex = new Map();
  for (const p of periods) {
    index.set(employmentKey(p.companyId, p.employeeId), p.spans);
  }
  return index;
};

/**
 * Pobiera przedziały zatrudnienia (etat + umowa cywilnoprawna) dla wskazanych par (companyId, employeeId)
 * w zakresie [from, to]. `from`/`to` w formacie "yyyy-MM-dd".
 */
export const getEmploymentPeriods = async (
  employees: { companyId: number; employeeId: number }[],
  from: string,
  to: string,
): Promise<EmploymentPeriods[]> => {
  const uniquePairs = Array.from(
    new Map(
      employees
        .filter((e) => e.companyId && e.employeeId)
        .map((e) => [employmentKey(e.companyId, e.employeeId), e]),
    ).values(),
  );

  if (uniquePairs.length === 0) {
    return [];
  }

  try {
    const { data } = await axios.post<EmploymentPeriods[]>(
      "/api/employment-periods",
      { employees: uniquePairs, from, to },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (
          error.response.data &&
          typeof error.response.data === "object" &&
          "message" in error.response.data
        ) {
          message = String(error.response.data.message);
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};
